import { Link, Stack, Typography, useTheme } from '@mui/material';
import {
  formatDateTime,
  joinString,
  printRoundedNumber,
} from '../../../shared/utils';
import { useNotificationContext } from '../notification';
import { ItemAPI } from '../../../api/item';
import TableWrapper from '../table';
import { dataTypes } from '../table-helpers';
import { PaddedCard } from '../ui-extension';
import { useP21UserContext } from '../../../context/p21-user-context';
import { ErpLink } from '../erp-link';

const poDataTypes = {
  purchaseOrderNumber: (permissions) => (poNumber, row) => {
    const displayText = `${poNumber}:${row.line_no}`;

    return permissions.isSupplyChain ? (
      <ErpLink
        windowName={'purchaseorderentry'}
        query={{
          po_no: poNumber,
        }}
        text={displayText}
        title={'Open in Purchase Order Entry'}
      />
    ) : (
      <Typography>
        {poNumber}:{row.line_no}
      </Typography>
    );
  },
  trucks: (trucks) => {
    if (trucks?.length && Array.isArray(trucks)) {
      return trucks.map((truck, i) => (
        <Typography key={i} variant='caption'>
          {truck.container_name}, QTY:{' '}
          {printRoundedNumber(truck.container_qty_received)}
        </Typography>
      ));
    }
    return '';
  },
  poStatus: (theme) => (description) => {
    const formattedStatus = description?.toLowerCase();

    const color = (() => {
      if (formattedStatus.includes('received'))
        return theme.palette.success.main;
      if (formattedStatus.includes('pending'))
        return theme.palette.secondary.main;

      return theme.palette.primary.main;
    })();

    return <Typography sx={{ color }}>{description}</Typography>;
  },
};

const PurchaseOrderDetails = ({
  quantityOnPo = 0,
  itemId,
  legacyLocationId,
  month,
  year,
  showCompleted = false,
}) => {
  const { useExceptionHandler, openDialog } = useNotificationContext();
  const { permissions } = useP21UserContext();
  const theme = useTheme();

  const loadPurchaseOrderDetails = useExceptionHandler(async () => {
    const { data: purchaseOrderDetails } =
      await ItemAPI.getPurchaseOrderDetails({
        itemId,
        legacyLocationId,
        month,
        year,
        showCompleted,
      });

    const hasDropShipPo = purchaseOrderDetails.some(
      (po) => po.legacy_location_id === 'DS'
    );

    const hasStockPo = purchaseOrderDetails.some(
      (po) => po.legacy_location_id !== 'DS'
    );

    openDialog({
      title: (
        <Stack>
          <Typography variant='body'>
            Purchase Orders for {itemId}
            {legacyLocationId
              ? ` - ${legacyLocationId}`
              : ' from All Locations'}
          </Typography>
          {month || year ? (
            <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
              Placed in{' '}
              {formatDateTime(
                new Date(year || 2000, (month || 1) - 1, 1),
                joinString([month ? 'MMMM' : '', year ? 'yyyy' : ''], ' ')
              )}
            </Typography>
          ) : (
            false
          )}
        </Stack>
      ),
      fullWidth: true,
      content: (
        <PaddedCard>
          <TableWrapper
            showSearch
            transformRows
            rows={purchaseOrderDetails}
            columns={[
              {
                id: 'po_no',
                label: 'PO / Line',
                dataType: poDataTypes.purchaseOrderNumber(permissions),
              },
              ...(purchaseOrderDetails.some((po) =>
                purchaseOrderDetails.some(
                  (po2) =>
                    po2.legacy_location_id &&
                    po2.legacy_location_id !== po.legacy_location_id
                )
              )
                ? [
                    {
                      id: 'legacy_location_id',
                      label: 'Location',
                      dataType: dataTypes.string,
                      isSortable: true,
                    },
                  ]
                : []),
              {
                id: 'unit_quantity',
                label: 'Quantity',
                dataType: dataTypes.quantity,
                isSortable: true,
              },
              {
                id: 'order_date',
                label: 'Date Ordered',
                isSortable: true,
                dataType: dataTypes.date,
              },
              {
                id: 'expected_date',
                label: 'Date Expected',
                isSortable: true,
                dataType: dataTypes.date,
              },
              {
                id: 'description',
                label: 'Status',
                dataType: poDataTypes.poStatus(theme),
              },
              ...(purchaseOrderDetails.some((po) => !!po.ocean_freight_status)
                ? [
                    {
                      id: 'ocean_freight_status',
                      label: 'Ocean Freight Status',
                      dataType: dataTypes.string,
                      isSortable: true,
                    },
                  ]
                : []),
              ...(purchaseOrderDetails.some((po) => !!po.trucks?.length)
                ? [
                    {
                      id: 'trucks',
                      label: 'Trucks',
                      dataType: poDataTypes.trucks,
                    },
                  ]
                : []),
              ...(hasStockPo
                ? [
                    {
                      id: 'buyer_name',
                      label: 'Buyer',
                      dataType: dataTypes.string,
                    },
                  ]
                : []),
              {
                id: 'supplier_name',
                label: 'Supplier',
                dataType: dataTypes.string,
              },
              ...(hasDropShipPo
                ? [
                    {
                      id: 'order_number',
                      label: 'SO',
                      isSortable: true,
                      dataType: dataTypes.salesOrderNumber,
                    },
                    {
                      id: 'consultant_name',
                      label: 'Consultant',
                      dataType: dataTypes.string,
                    },
                    {
                      id: 'customer_name',
                      label: 'Customer',
                      dataType: dataTypes.string,
                    },
                    {
                      id: 'customer_po',
                      label: 'Customer PO',
                      dataType: dataTypes.string,
                    },
                  ]
                : []),
            ]}
          />
        </PaddedCard>
      ),
      hideActions: true,
      // This is a read-only list, so a backdrop click can safely close it.
      closeOnBackdrop: true,
    });
  });

  return quantityOnPo > 0 ? (
    <Link
      onClick={() => {
        loadPurchaseOrderDetails();
      }}
    >
      {printRoundedNumber(quantityOnPo, 0)}
    </Link>
  ) : (
    <Typography>0</Typography>
  );
};

export default PurchaseOrderDetails;
